import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import type { Task } from '../../../lib/db/models'
import type { OptimisticUpdates } from './useTaskDragAndDrop'

/** Bestätigt die Datenbank einen Wert nie (etwas anderes hat die Zeile umgeschrieben), gilt er so lange. */
const ORDER_HARD_TIMEOUT_MS = 10_000

/**
 * **Die vorläufige Reihenfolge nach Ziehen und ⌥↑/↓** — bis die Datenbank sie bestätigt.
 *
 * Ein Eintrag fällt weg, sobald die **rohe** Aufgabe (nicht die überschriebene) denselben
 * `order` und, falls gesetzt, denselben Abschnitt trägt; fehlt die Aufgabe ganz, ebenso.
 * Bis 28.09.2026 löschte ein fester Timer 100 ms nach dem Loslassen alles auf einmal. Kam die
 * Datenbank langsamer nach (Dev-Modus, Sync-Warteschlange je Zeile), stand die Zeile ein Bild
 * lang wieder an ihrem Ursprung und sprang dann ans Ziel — ein Zittern am Ende jedes Ziehens.
 *
 * Gleiche Form wie `useState<OptimisticUpdates>`, damit Ziehen, Tastatur und Abschnitte den
 * Setter unverändert weiterbenutzen.
 */
export function useOptimisticOrder(rawTasks: readonly Task[] | undefined): [OptimisticUpdates, Dispatch<SetStateAction<OptimisticUpdates>>] {
  const [updates, setUpdatesState] = useState<OptimisticUpdates>(null)
  const setAtRef = useRef(0)

  const setUpdates = useCallback<Dispatch<SetStateAction<OptimisticUpdates>>>(action => {
    setAtRef.current = Date.now()
    setUpdatesState(action)
  }, [])

  useEffect(() => {
    if (!updates || !rawTasks) return
    const rawById = new Map(rawTasks.map(task => [task.id, task]))
    const next = new Map(updates)
    for (const [id, update] of updates) {
      const task = rawById.get(id)
      const sectionMatches = update.sectionId === undefined || (task?.sectionId ?? null) === (update.sectionId ?? null)
      if (!task || (task.order === update.order && sectionMatches)) next.delete(id)
    }
    if (next.size !== updates.size) setUpdatesState(next.size > 0 ? next : null)
  }, [rawTasks, updates])

  useEffect(() => {
    if (!updates) return
    const timer = setTimeout(() => setUpdatesState(null), Math.max(0, setAtRef.current + ORDER_HARD_TIMEOUT_MS - Date.now()))
    return () => clearTimeout(timer)
  }, [updates])

  return [updates, setUpdates]
}
